import React, {useState} from 'react';
import {Box, Skeleton, Tooltip} from "@mui/material";
import {ActionsMenu} from "@dreebit-registry/vsm-microservice-components";
import {IconButton, Typography} from "@mui/joy";
import {
    AddRounded,
    MoreVertRounded,
} from "@mui-symbols-material/w300";
import {InstalledBaseNode, InstalledBaseNodeType, InstalledBaseNodeTypeKey} from "../../codegen/graphql";
import InstalledBaseSearchFilter from "./searchFilter";
import InstalledBaseNodeTypeIcon from "../InstalledBaseNodeTypeIcon";
import {useNodeActions} from "./nodeActions";
import {MoveInstalledBaseNodeSuccessInfo} from "../MoveInstalledBaseNodeModal";
import {useTranslation} from "react-i18next";
import {getInstalledBaseNodeTypeName} from "../../utils/getInstalledBaseNodeTypeName";
import {canAddAnyChild, canOfferChildCreation, childRuleHasRoom} from "../../utils/nodeChildRules";
import {isNodeInTrash} from "../../utils/installedBasePath";

interface Props {
    parentId: string | null
    parentNode: InstalledBaseNode
    searchQuery: string
    onSearch: (query: string) => void
    isLoading?: boolean
    refetchFunction?: () => void
    deleteNodeCallback?: (deletedNodeId: string) => void
    moveNodeCallback?: (info: MoveInstalledBaseNodeSuccessInfo) => void
    onEditNodeDetail?: (nodeId: string) => void
    isFilterOpen: boolean
    onShowFilterContent: (value: boolean) => void
    activeFilterCount?: number
    onOpenCreateModal: () => void
}

const InstalledBaseTreeColumnHeader = ({
                               parentId,
                               parentNode,
                               searchQuery,
                               onSearch,
                               isLoading,
                               refetchFunction,
                               deleteNodeCallback,
                               moveNodeCallback,
                               onEditNodeDetail,
                               isFilterOpen,
                               onShowFilterContent,
                               activeFilterCount,
                               onOpenCreateModal
}: Props) => {
    const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
    const {t} = useTranslation();

    const parentNodeType: InstalledBaseNodeType = parentNode?.nodeType;

    const typeName = getInstalledBaseNodeTypeName(parentNodeType, t);

    // IBM-586: true for the TRASH node itself and anything below it — no creation
    // here, and useNodeActions strips everything except Restore + Delete.
    const columnInTrash = isNodeInTrash(parentNode);

    const { items: actionItems, modals } = useNodeActions({
        targetNode: parentNode,
        isRoot: parentId === null,
        context: 'header',
        inTrash: columnInTrash,
        refetchFunction,
        deleteNodeCallback,
        moveNodeCallback,
        onEditNode: onEditNodeDetail && parentId
            ? () => onEditNodeDetail(parentId)
            : undefined,
    });

    // Structural rules (IBM-462): leaf nodes get no "+" at all; when every allowed
    // child type is at its count limit the "+" is disabled with an explanatory hint.
    // While the parent query is loading, nodeRules is undefined → button stays enabled.
    // Creating a child modifies the parent's subtree, so it takes the same `write`
    // permission that gates edit/move (see nodeActions.tsx).
    const nodeRules = parentNode?.nodeRules;
    const showAddButton = !columnInTrash
        && canOfferChildCreation(parentId === null, nodeRules, !!parentNode?.access?.write);
    const addDisabled = !canAddAnyChild(nodeRules);
    const limitHint = (nodeRules?.childRules ?? [])
        .filter((rule) => rule.limits && !childRuleHasRoom(rule))
        .map((rule) => {
            const counts = {current: rule.currentNodeCount, max: rule.limits!.max};
            if (rule.nodeTypeKey === InstalledBaseNodeTypeKey.PRESSURE_LINE) {
                return t('Pressure line limit reached ({{current}}/{{max}})', counts);
            }
            if (rule.nodeTypeKey === InstalledBaseNodeTypeKey.ASSET) {
                return t('Asset limit reached ({{current}}/{{max}})', counts);
            }
            return t('Limit reached ({{current}}/{{max}})', counts);
        })
        .join(', ');

    return (
        <>
            <Box
                className={"flex-col full-width"}
                sx={{
                    p: "4px 20px 20px 20px",
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 1,
                }}
            >
                <Box
                    className={"flex-row full-width"}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        minHeight: "2.25rem",
                    }}
                >
                    <Box className={"flex-row"} sx={{alignItems: "center", overflow: "hidden"}}>
                        {
                            isLoading ? <Skeleton  sx={{ width: 200 }} /> :
                                <Tooltip title={parentNode?.title ?? 'No node available'} placement={'top-start'}
                                         enterDelay={700} enterNextDelay={500}>
                                    <Typography
                                        level={'titleMedium'}
                                        noWrap
                                        sx={{
                                            width: "100%",
                                        }}>
                                        {parentNode?.title ?? 'No node available'}
                                    </Typography>
                                </Tooltip>
                        }
                    </Box>

                    <Box className={`flex-row installed-base-header-actions ${actionsMenuOpen ? 'actions-open' : ''}`}>
                        {showAddButton && (
                            addDisabled ?
                                <Tooltip title={limitHint}>
                                    {/* span keeps the tooltip working while the button is disabled */}
                                    <span style={{display: 'inline-flex'}}>
                                        <IconButton disabled>
                                            <AddRounded fontSize={'small'}/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                :
                                <IconButton onClick={onOpenCreateModal}>
                                    <AddRounded fontSize={'small'}/>
                                </IconButton>
                        )}
                        {parentId !== null &&
                            <ActionsMenu
                                items={actionItems}
                                icon={<MoreVertRounded fontSize={'small'}/>}
                                onOpenChange={(open: boolean) => setActionsMenuOpen(open)}
                            />
                        }
                    </Box>
                </Box>

                {/* Node type subtitle. Always rendered (with reserved height) so all column
                    headers stay the same height; the root "Companies" column has no subtitle. */}
                <Box sx={{display: 'flex', alignItems: 'center', gap: '4px', minHeight: '14px', mt: '-5px'}}>
                    {!isLoading && parentId !== null &&
                        <>
                            <Box className={'flex'} sx={{flexShrink: 0}}>
                                <InstalledBaseNodeTypeIcon
                                    nodeTypeKey={parentNodeType?.key}
                                    size={14}
                                    color={'#666666'}
                                    starred={parentNode?.isStarred}
                                />
                            </Box>
                            <Typography level={'caption'} noWrap sx={{color: '#666666'}}>
                                {typeName}
                            </Typography>
                        </>
                    }
                </Box>

                {parentNodeType?.key !== "ASSET" &&
                    <Box sx={{pt: "8px"}}>
                        <InstalledBaseSearchFilter
                            searchQuery={searchQuery}
                            onSearch={onSearch}
                            isFilterOpen={isFilterOpen}
                            onShowFilterContent={onShowFilterContent}
                            activeFilterCount={activeFilterCount}
                        />
                    </Box>
                }
            </Box>

            {modals}
        </>
    );
}

export default InstalledBaseTreeColumnHeader;
